/**
 * Supabase Auth Adapter
 *
 * Implements AuthProvider using the Supabase JS SDK.
 * This is the only file that imports @supabase/supabase-js.
 *
 * All Supabase-specific error handling lives here — the adapter normalizes
 * errors into AuthProviderError so consumers never see Supabase types.
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';
import AsyncStorage from '@react-native-async-storage/async-storage';
import i18n from '../../i18n';
import type {
  AuthProvider,
  AuthUser,
  AuthSession,
  AuthResult,
  SignUpResult,
  AuthEventType,
  AuthErrorCode,
} from '../types';
import { AuthProviderError } from '../types';

// ─── Supabase error code → AuthErrorCode mapping ─────────────────

const SUPABASE_ERROR_MAP: Record<string, { code: AuthErrorCode; i18nKey: string; fallback: string }> = {
  // Login
  invalid_credentials: {
    code: 'INVALID_CREDENTIALS',
    i18nKey: 'errors.auth.INVALID_CREDENTIALS',
    fallback: 'Invalid email or password. Please try again.',
  },
  user_not_found: {
    code: 'USER_NOT_FOUND',
    i18nKey: 'errors.auth.USER_NOT_FOUND',
    fallback: 'No account found with this email address.',
  },
  email_not_confirmed: {
    code: 'EMAIL_NOT_CONFIRMED',
    i18nKey: 'errors.auth.EMAIL_NOT_CONFIRMED',
    fallback: 'Please verify your email before logging in. Check your inbox for the confirmation link.',
  },
  // Registration
  user_already_exists: {
    code: 'USER_ALREADY_EXISTS',
    i18nKey: 'errors.auth.USER_ALREADY_EXISTS',
    fallback: 'An account with this email already exists. Try logging in instead.',
  },
  email_exists: {
    code: 'USER_ALREADY_EXISTS',
    i18nKey: 'errors.auth.USER_ALREADY_EXISTS',
    fallback: 'An account with this email already exists. Try logging in instead.',
  },
  weak_password: {
    code: 'WEAK_PASSWORD',
    i18nKey: 'errors.auth.WEAK_PASSWORD',
    fallback: 'Password is too weak. Please use at least 8 characters with a mix of letters and numbers.',
  },
  // Rate limiting
  over_request_rate_limit: {
    code: 'RATE_LIMITED',
    i18nKey: 'errors.auth.RATE_LIMITED',
    fallback: 'Too many attempts. Please wait a moment and try again.',
  },
  // Resend raced inside Supabase's max_frequency window (60s per address)
  over_email_send_rate_limit: {
    code: 'RATE_LIMITED',
    i18nKey: 'errors.auth.RATE_LIMITED',
    fallback: 'Too many attempts. Please wait a moment and try again.',
  },
  // Password
  same_password: {
    code: 'SAME_PASSWORD',
    i18nKey: 'errors.auth.SAME_PASSWORD',
    fallback: 'New password must be different from your current password.',
  },
  otp_expired: {
    code: 'RESET_LINK_EXPIRED',
    i18nKey: 'errors.auth.RESET_LINK_EXPIRED',
    fallback: 'Reset link has expired. Please request a new one.',
  },
  // Session
  session_not_found: {
    code: 'SESSION_EXPIRED',
    i18nKey: 'errors.auth.SESSION_EXPIRED',
    fallback: 'Your session has expired. Please log in again.',
  },
  refresh_token_not_found: {
    code: 'SESSION_EXPIRED',
    i18nKey: 'errors.auth.SESSION_EXPIRED',
    fallback: 'Your session has expired. Please log in again.',
  },
};

/**
 * Supabase reports a wrong AND an expired recovery code with the same
 * `otp_expired` code. On the typed-code path that must not read as
 * "reset link has expired" — this override remaps it to the code-specific
 * error while the shared map keeps the link wording for the deep-link path.
 */
const RECOVERY_CODE_ERROR_OVERRIDES: typeof SUPABASE_ERROR_MAP = {
  otp_expired: {
    code: 'RECOVERY_CODE_INVALID',
    i18nKey: 'errors.auth.RECOVERY_CODE_INVALID',
    fallback: 'That code is invalid or has expired. Request a new one.',
  },
};

/**
 * Same `otp_expired` ambiguity on the sign-up confirmation path (AUTH-013):
 * a wrong and an expired code are reported identically, and neither should
 * read as "reset link has expired".
 */
const SIGNUP_CODE_ERROR_OVERRIDES: typeof SUPABASE_ERROR_MAP = {
  otp_expired: {
    code: 'SIGNUP_CODE_INVALID',
    i18nKey: 'errors.auth.SIGNUP_CODE_INVALID',
    fallback: 'That code is invalid or has expired. Request a new one.',
  },
};

/**
 * Normalize a Supabase error into an AuthProviderError.
 * All error knowledge stays in this adapter — the store never sees Supabase types.
 * `overrides` lets a call site remap a Supabase code whose meaning depends on
 * context (e.g. `otp_expired` on the typed-code path vs the link path).
 */
function normalizeError(
  error: unknown,
  overrides?: typeof SUPABASE_ERROR_MAP,
): AuthProviderError {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code?: string }).code;
    const mapped = code ? overrides?.[code] ?? SUPABASE_ERROR_MAP[code] : undefined;
    if (mapped) {
      const message = i18n.exists(mapped.i18nKey)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dynamic key from SUPABASE_ERROR_MAP, checked with i18n.exists()
        ? i18n.t(mapped.i18nKey as any)
        : mapped.fallback;
      return new AuthProviderError(mapped.code, message);
    }
  }

  // Fallback: use the raw message if available
  const rawMessage = error instanceof Error ? error.message : String(error);
  const fallbackMessage = i18n.exists('errors.auth.UNKNOWN')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dynamic error i18n key
    ? i18n.t('errors.auth.UNKNOWN' as any)
    : 'An authentication error occurred. Please try again.';

  return new AuthProviderError('UNKNOWN', rawMessage || fallbackMessage);
}

// ─── Data mappers ────────────────────────────────────────────────

function toAuthUser(supabaseUser: {
  id: string;
  email?: string | null;
  user_metadata?: Record<string, unknown>;
}): AuthUser {
  return {
    id: supabaseUser.id,
    email: supabaseUser.email!,
    name:
      (supabaseUser.user_metadata?.name as string) ||
      (supabaseUser.user_metadata?.full_name as string) ||
      undefined,
  };
}

function toAuthSession(supabaseSession: {
  access_token: string;
  refresh_token?: string;
  expires_at?: number;
}): AuthSession {
  return {
    accessToken: supabaseSession.access_token,
    refreshToken: supabaseSession.refresh_token,
    expiresAt: supabaseSession.expires_at,
    raw: supabaseSession,
  };
}

const EVENT_MAP: Record<string, AuthEventType | undefined> = {
  SIGNED_IN: 'SIGNED_IN',
  SIGNED_OUT: 'SIGNED_OUT',
  TOKEN_REFRESHED: 'TOKEN_REFRESHED',
  USER_UPDATED: 'USER_UPDATED',
  PASSWORD_RECOVERY: 'PASSWORD_RECOVERY',
};

// ─── Adapter ─────────────────────────────────────────────────────

export function createSupabaseAdapter(
  supabaseUrl: string,
  supabasePublishableKey: string,
): AuthProvider {
  let client: SupabaseClient | null = null;

  function getClient(): SupabaseClient {
    if (!client) {
      if (!supabaseUrl || !supabasePublishableKey) {
        throw new Error(
          'Supabase configuration missing. These arrive through expoConfig.extra ' +
            '(see app.config.ts) — check SUPABASE_URL and SUPABASE_PUBLISHABLE_KEY.',
        );
      }
      client = createClient(supabaseUrl, supabasePublishableKey, {
        auth: {
          storage: AsyncStorage,
          autoRefreshToken: true,
          persistSession: true,
          detectSessionInUrl: false,
        },
      });
    }
    return client;
  }

  const adapter: AuthProvider = {
    async signIn(email, password): Promise<AuthResult> {
      const { data, error } = await getClient().auth.signInWithPassword({ email, password });
      if (error) throw normalizeError(error);
      return {
        user: toAuthUser(data.user!),
        session: toAuthSession(data.session!),
      };
    },

    async signUp(email, password, metadata): Promise<SignUpResult> {
      const { data, error } = await getClient().auth.signUp({
        email,
        password,
        options: metadata ? { data: metadata } : undefined,
      });
      if (error) throw normalizeError(error);

      return {
        user: toAuthUser(data.user!),
        session: data.session ? toAuthSession(data.session) : null,
        emailConfirmationRequired: !!(data.user && !data.session),
      };
    },

    async signOut(): Promise<void> {
      const { error } = await getClient().auth.signOut();
      if (error) throw normalizeError(error);
    },

    async getSession() {
      const { data: { session } } = await getClient().auth.getSession();
      if (!session?.user) return null;
      return {
        user: toAuthUser(session.user),
        session: toAuthSession(session),
      };
    },

    async refreshSession(): Promise<AuthSession | null> {
      const { data, error } = await getClient().auth.refreshSession();
      if (error || !data.session) return null;
      return toAuthSession(data.session);
    },

    async resetPassword(email, options) {
      const { error } = await getClient().auth.resetPasswordForEmail(email, {
        redirectTo: options?.redirectTo,
      });
      if (error) throw normalizeError(error);
    },

    async verifyRecoveryCode(email, code): Promise<AuthResult> {
      const { data, error } = await getClient().auth.verifyOtp({
        email,
        token: code,
        type: 'recovery',
      });
      if (error) throw normalizeError(error, RECOVERY_CODE_ERROR_OVERRIDES);
      return {
        user: toAuthUser(data.user!),
        session: toAuthSession(data.session!),
      };
    },

    async verifySignupCode(email, code): Promise<AuthResult> {
      const { data, error } = await getClient().auth.verifyOtp({
        email,
        token: code,
        type: 'signup',
      });
      if (error) throw normalizeError(error, SIGNUP_CODE_ERROR_OVERRIDES);
      return {
        user: toAuthUser(data.user!),
        session: toAuthSession(data.session!),
      };
    },

    async resendSignupConfirmation(email): Promise<void> {
      const { error } = await getClient().auth.resend({ type: 'signup', email });
      if (error) throw normalizeError(error);
    },

    async updatePassword(newPassword) {
      const { error } = await getClient().auth.updateUser({ password: newPassword });
      if (error) throw normalizeError(error);
    },

    async updateUserMetadata(data) {
      const { error } = await getClient().auth.updateUser({ data });
      if (error) throw normalizeError(error);
    },

    onAuthStateChange(callback) {
      const { data: { subscription } } = getClient().auth.onAuthStateChange((event, session) => {
        const mappedEvent = EVENT_MAP[event];
        if (!mappedEvent) return;

        const user = session?.user ? toAuthUser(session.user) : null;
        const authSession = session ? toAuthSession(session) : null;
        callback(mappedEvent, user, authSession);
      });
      return () => subscription.unsubscribe();
    },

    async getAccessToken(): Promise<string | null> {
      const { data: { session } } = await getClient().auth.getSession();
      return session?.access_token ?? null;
    },
  };

  return adapter;
}
